import "twin.macro";

import { Check, ChevronDown } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

export type SortMenuOption = {
  id: string;
  label: string;
};

export default function SortMenuMd({
  options,
  value,
  onChange,
  buttonLabel = "Classer par",
}: {
  options: readonly SortMenuOption[];
  value: string;
  onChange: (nextId: string) => void;
  buttonLabel?: string;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  const activeLabel = useMemo(() => {
    return options.find((o) => o.id === value)?.label ?? "";
  }, [options, value]);

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };

    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node | null;
      if (!target) return;
      if (!rootRef.current?.contains(target)) setOpen(false);
    };

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("pointerdown", onPointerDown);

    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("pointerdown", onPointerDown);
    };
  }, [open]);

  if (options.length === 0) return null;

  return (
    <div ref={rootRef} tw="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        tw="inline-flex items-center gap-2 h-10 rounded-xl border border-gray-200 bg-white px-3 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-colors focus:(ring-2 ring-primary-200 border-primary-300)"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <span tw="whitespace-nowrap">
          {buttonLabel}
          {activeLabel ? `: ${activeLabel}` : ""}
        </span>
        <ChevronDown tw="w-4 h-4 opacity-70" />
      </button>

      {open && (
        <div
          ref={menuRef}
          role="menu"
          tw="absolute right-0 mt-2 min-w-56 rounded-xl border border-gray-200 bg-white shadow-lg overflow-hidden z-50"
        >
          {options.map((o) => {
            const selected = o.id === value;
            return (
              <button
                key={o.id}
                type="button"
                role="menuitemradio"
                aria-checked={selected}
                onClick={() => {
                  onChange(o.id);
                  setOpen(false);
                }}
                tw="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-800 hover:bg-gray-50 transition-colors"
              >
                <span tw="w-4 flex items-center justify-center shrink-0">
                  {selected ? <Check tw="w-4 h-4" /> : null}
                </span>
                <span tw="truncate">{o.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
